import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, MagnifyingGlass, Clock, CaretLeft, CaretRight } from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import SEOHead from '../components/SEOHead';
import PublicNav from '../components/PublicNav';
import PublicFooter from '../components/PublicFooter';
import { usePublicT } from '../i18n/usePublicT';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function BlogPage() {
  const navigate = useNavigate();
  const pt = usePublicT();
  const [searchParams, setSearchParams] = useSearchParams();
  const [posts, setPosts] = useState([]);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  const [categories, setCategories] = useState([]);
  const [allTags, setAllTags] = useState([]);
  const [loading, setLoading] = useState(true);

  const page = parseInt(searchParams.get('page') || '1');
  const category = searchParams.get('category') || '';
  const search = searchParams.get('q') || '';
  const [searchInput, setSearchInput] = useState(search);

  useEffect(() => {
    const fetchPosts = async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams({ page, limit: 12 });
        if (category) params.append('category', category);
        if (search) params.append('search', search);
        const res = await axios.get(`${API_URL}/api/blog/posts?${params}`);
        setPosts(res.data.posts);
        setTotal(res.data.total);
        setPages(res.data.pages);
        setCategories(res.data.categories || []);
        setAllTags(res.data.all_tags || []);
      } catch { setPosts([]); }
      setLoading(false);
    };
    fetchPosts();
  }, [page, category, search]);

  const handleSearch = (e) => {
    e.preventDefault();
    setSearchParams({ q: searchInput, page: '1' });
  };

  const setCategory = (cat) => {
    const params = {};
    if (cat) params.category = cat;
    params.page = '1';
    setSearchParams(params);
  };

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Blog',
    name: 'Track My Package Blog',
    url: 'https://trackmypackage.app/blog',
    description: 'Expert insights on courier business, shipping guides, package tracking, and logistics technology.',
    publisher: { '@type': 'Organization', name: 'Track My Package' },
  };

  return (
    <div className="min-h-screen bg-[#0B1120]" data-testid="blog-page">
      <SEOHead
        title="Blog — Courier Business Tips, Shipping Guides & Logistics Insights"
        description="Expert articles on running a courier business, shipping best practices, package tracking technology, and logistics industry trends. Updated weekly."
        path="/blog"
        keywords="courier business blog, shipping tips, logistics insights, package tracking guide, courier industry trends, shipping best practices"
        jsonLd={jsonLd}
      />

      <PublicNav activePage="/blog" />

      {/* Hero */}
      <section className="pt-28 pb-8 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <motion.h1 className="text-4xl sm:text-5xl font-bold tracking-tight text-white leading-tight"
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            Courier & Logistics<br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#FF9900] to-[#FFB060]">Industry Insights</span>
          </motion.h1>
          <motion.p className="text-gray-400 mt-4 text-base sm:text-lg max-w-2xl mx-auto"
            initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
            {pt('blog.subtitle')}
          </motion.p>

          {/* Search */}
          <motion.form onSubmit={handleSearch} className="mt-6 max-w-lg mx-auto flex gap-2"
            initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} data-testid="blog-search-form">
            <div className="relative flex-1">
              <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
              <Input value={searchInput} onChange={e => setSearchInput(e.target.value)}
                placeholder={pt('blog.search')}
                className="pl-10 h-11 rounded-xl bg-white/5 border-white/10 text-white placeholder:text-gray-500 focus:border-[#FF9900]"
                data-testid="blog-search-input" />
            </div>
            <Button type="submit" className="h-11 px-5 rounded-xl bg-[#FF9900] hover:bg-[#FF9900]/90 text-white" data-testid="blog-search-btn">{pt('hero.trackBtn')}</Button>
          </motion.form>
        </div>
      </section>

      {/* Categories */}
      {categories.length > 0 && (
        <section className="px-6 pb-6">
          <div className="max-w-6xl mx-auto flex flex-wrap gap-2 justify-center" data-testid="blog-categories">
            <button onClick={() => setCategory('')}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${!category ? 'bg-[#FF9900] text-white' : 'bg-white/5 text-gray-400 hover:text-white'}`}
              data-testid="blog-cat-all">
              {pt('blog.allCategories')}
            </button>
            {categories.map(cat => (
              <button key={cat} onClick={() => setCategory(cat)}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${category === cat ? 'bg-[#FF9900] text-white' : 'bg-white/5 text-gray-400 hover:text-white'}`}
                data-testid={`blog-cat-${cat.toLowerCase().replace(/\s/g, '-')}`}>
                {cat}
              </button>
            ))}
          </div>
        </section>
      )}

      {/* Articles Grid */}
      <section className="py-8 px-6">
        <div className="max-w-6xl mx-auto">
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              {[1, 2, 3, 4, 5, 6].map(i => (
                <div key={i} className="bg-white/[0.03] border border-white/10 rounded-2xl p-5 animate-pulse">
                  <div className="h-4 bg-white/10 rounded w-1/3 mb-3" />
                  <div className="h-6 bg-white/10 rounded w-full mb-2" />
                  <div className="h-4 bg-white/5 rounded w-full mb-1" />
                  <div className="h-4 bg-white/5 rounded w-2/3" />
                </div>
              ))}
            </div>
          ) : posts.length === 0 ? (
            <div className="text-center py-16">
              <p className="text-gray-400 text-lg mb-2">{pt('blog.noArticles')}</p>
              <p className="text-gray-500 text-sm">New articles are published regularly.</p>
              {(search || category) && (
                <Button onClick={() => { setSearchInput(''); setSearchParams({}); }} className="mt-4 bg-white/5 text-gray-300 rounded-xl">Clear Filters</Button>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="blog-grid">
              {posts.map((post, i) => (
                <motion.article key={post.id || i}
                  initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
                  transition={{ delay: i * 0.05 }}
                  onClick={() => navigate(`/blog/${post.slug}`)}
                  className="bg-white/[0.03] border border-white/10 rounded-2xl p-5 hover:border-[#FF9900]/30 transition-all cursor-pointer group"
                  data-testid={`blog-card-${i}`}>
                  <div className="flex items-center gap-2 mb-3">
                    <span className="px-2 py-0.5 bg-[#FF9900]/10 text-[#FF9900] text-[10px] font-semibold rounded-full uppercase tracking-wide">
                      {post.category || 'General'}
                    </span>
                    <span className="flex items-center gap-1 text-gray-500 text-[10px]">
                      <Clock size={10} /> {post.read_time_minutes || 5} min read
                    </span>
                  </div>
                  <h2 className="font-bold text-white text-base mb-2 group-hover:text-[#FF9900] transition-colors leading-snug">
                    {post.title}
                  </h2>
                  <p className="text-sm text-gray-400 line-clamp-3 mb-3">{post.excerpt}</p>
                  <div className="flex items-center justify-between">
                    <div className="flex gap-1.5">
                      {(post.tags || []).slice(0, 2).map(tag => (
                        <span key={tag} className="px-1.5 py-0.5 bg-white/5 text-gray-500 text-[9px] rounded">
                          {tag}
                        </span>
                      ))}
                    </div>
                    <span className="text-[10px] text-gray-500">
                      {post.published_at ? new Date(post.published_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : ''}
                    </span>
                  </div>
                </motion.article>
              ))}
            </div>
          )}

          {/* Pagination */}
          {pages > 1 && (
            <div className="flex items-center justify-center gap-2 mt-10" data-testid="blog-pagination">
              <Button disabled={page <= 1} onClick={() => setSearchParams({ ...Object.fromEntries(searchParams), page: String(page - 1) })}
                className="bg-white/5 text-gray-300 rounded-lg" variant="ghost">
                <CaretLeft size={16} /> Prev
              </Button>
              <span className="text-gray-400 text-sm px-3">Page {page} of {pages}</span>
              <Button disabled={page >= pages} onClick={() => setSearchParams({ ...Object.fromEntries(searchParams), page: String(page + 1) })}
                className="bg-white/5 text-gray-300 rounded-lg" variant="ghost">
                Next <CaretRight size={16} />
              </Button>
            </div>
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 px-6 bg-gradient-to-r from-[#003166] to-[#0077FF]">
        <div className="max-w-2xl mx-auto text-center">
          <h2 className="text-2xl sm:text-3xl font-bold text-white mb-3">{pt('cta.growTitle')}</h2>
          <p className="text-white/60 mb-6 text-base">{pt('cta.growSubtitle')}</p>
          <Button onClick={() => window.open('https://sqawk.app/book/MrPositivity', '_blank')}
            className="bg-[#FF9900] hover:bg-[#FF9900]/90 text-white text-base px-8 py-5 rounded-xl" data-testid="blog-cta-btn">
            {pt('nav.scheduleCall')} <ArrowRight size={18} className="ml-2" />
          </Button>
        </div>
      </section>

      <PublicFooter />
    </div>
  );
}
